'use client';

import { ProjectChip } from './project-chip';
import { cn } from '../../lib/utils';
import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { useEffect, useRef } from 'react';
import { type Project } from '@/app/lib/data';
import { useTranslation } from 'react-i18next';

/**
 * LatestProjects component
 * Displays a section with the latest projects as chips
 * Each chip has an icon, name, and plus button
 * Enhanced with animations and styling consistent with the rest of the portfolio
 */
export interface LatestProjectsProps {
  projects: Project[];
  className?: string;
}

export function LatestProjects({ projects, className }: LatestProjectsProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tl = gsap.timeline();

    if (containerRef.current) gsap.set(containerRef.current, { opacity: 0 });
    tl.to(containerRef.current, { opacity: 1, duration: 0.4 });

    if (headerRef.current) {
      tl.fromTo(headerRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2");
    }

    if (iconRef.current) {
      gsap.to(iconRef.current, { y: -5, duration: 2, repeat: -1, yoyo: true, ease: "sine.inOut" });
    }

    if (listRef.current) {
      tl.fromTo(listRef.current.children,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.1, ease: "power2.out" },
        "-=0.2"
      );
    }
  }, []);

  if (!projects || projects.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={cn('space-y-4', className)}
    >
      <div
        ref={headerRef}
        className="flex items-center gap-2"
      >
        <div ref={iconRef}>
          <Icon
            icon="carbon:recently-added"
            className="h-5 w-5 text-slate-600 dark:text-slate-400"
          />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">{t('Projects.latest')}</h2>
        <div className="h-px flex-grow bg-slate-200 dark:bg-slate-700/50"></div>
      </div>

      <div
        ref={listRef}
        className="flex flex-wrap gap-2 py-2"
      >
        {projects.map((project, index) => (
          <div
            key={project._id}
            className="transition-all duration-200 hover:-translate-y-1 active:scale-95"
          >
            <ProjectChip
              label={project.translationKey ? t(`Projects.items.${project.translationKey}.title`) : project.title}
              iconName={project.iconName}
              onClick={() => window.location.href = `/projects/${project.slug?.current || ''}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
} 